/* global google */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { useJsApiLoader, GoogleMap, Marker } from "@react-google-maps/api";
import { toast } from "react-toastify";

const initialCenter = { lat: 22.572645, lng: 88.363892 }; // Mumbai
const libs = ["places"];

// helper: find address component by type
const findComponent = (components = [], type) =>
  (components.find((comp) => comp.types && comp.types.indexOf(type) > -1) || {})
    .long_name || "";

const AddDeliveryAddress = ({ onSubmit }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libs,
    version: "weekly",
  });

  const customer = JSON.parse(sessionStorage.getItem("active-user"));
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [addressLine, setAddressLine] = useState("");
  const [city, setCity] = useState("");
  const [downtown, setDowntown] = useState("");
  const [customerId] = useState(customer?.id);

  const autocompleteContainerRef = useRef(null);
  const placeElementRef = useRef(null);
  const mapRef = useRef(null);

  // map click handler
  const onMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setSelectedLocation({ lat, lng });
  }, []);

  // Use browser geolocation to set marker
  const useMyCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser", {
        autoClose: 1200,
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setSelectedLocation({ lat, lng });

        // Optionally reverse-geocode to fill address (if you want)
        // keep UX simple: user can refine address via autocomplete or manual text.
      },
      (err) => {
        console.error("geolocation error", err);
        toast.error("Unable to retrieve your location", { autoClose: 1200 });
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // Create PlaceAutocompleteElement using google.maps.importLibrary("places")
  useEffect(() => {
    let mounted = true;
    if (!isLoaded) return;

    (async () => {
      try {
        const placesLib = await google.maps.importLibrary("places");
        const PlaceAutocompleteElement = placesLib.PlaceAutocompleteElement;

        if (!mounted) return;

        // create the element and append to container
        const placeAutocomplete = new PlaceAutocompleteElement();
        placeElementRef.current = placeAutocomplete;

        // style host element lightly so it sits well with Bootstrap card
        try {
          placeAutocomplete.style.width = "100%";
          placeAutocomplete.style.boxSizing = "border-box";
          placeAutocomplete.style.fontFamily = "inherit";
          placeAutocomplete.setAttribute("aria-label", "Address search");
        } catch (err) {
          // non-critical - ignore
        }

        if (autocompleteContainerRef.current) {
          autocompleteContainerRef.current.innerHTML = "";
          autocompleteContainerRef.current.appendChild(placeAutocomplete);
        }

        // selection listener
        const onSelect = (ev) => {
          const place = ev?.detail?.place;
          if (!place) {
            toast.error("No details available for that selection", {
              autoClose: 1200,
            });
            return;
          }

          const formatted = place.formatted_address || place.name || "";
          setAddressLine(formatted);

          const components = place.address_components || [];
          const locality =
            findComponent(components, "locality") ||
            findComponent(components, "postal_town") ||
            findComponent(components, "administrative_area_level_2") ||
            findComponent(components, "administrative_area_level_1") ||
            "";
          setCity(locality);

          const neighborhood =
            findComponent(components, "neighborhood") ||
            findComponent(components, "sublocality_level_1") ||
            findComponent(components, "sublocality") ||
            "";
          setDowntown(neighborhood);

          if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setSelectedLocation({ lat, lng });

            // center map on selection (if map instance available)
            if (mapRef.current && mapRef.current.panTo) {
              mapRef.current.panTo({ lat, lng });
              mapRef.current.setZoom(15);
            }
          }
        };

        placeAutocomplete.addEventListener("gmp-select", onSelect);

        // cleanup
        return () => {
          try {
            placeAutocomplete.removeEventListener("gmp-select", onSelect);
            if (placeAutocomplete.parentNode)
              placeAutocomplete.parentNode.removeChild(placeAutocomplete);
          } catch (e) {
            /* ignore */
          }
        };
      } catch (err) {
        console.error("places import/create error", err);
        toast.error(
          "Failed to initialize address search (check API & billing)",
          { autoClose: 2400 }
        );
      }
    })();

    return () => {
      mounted = false;
      try {
        if (placeElementRef.current && placeElementRef.current.parentNode)
          placeElementRef.current.parentNode.removeChild(
            placeElementRef.current
          );
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  if (loadError) {
    return (
      <Container className="p-4">
        <div className="alert alert-danger">
          Google Maps failed to load. Check API key, billing, and console for
          details.
        </div>
      </Container>
    );
  }

  if (!isLoaded) {
    return (
      <Container className="p-4">
        <div>Loading Google Maps...</div>
      </Container>
    );
  }

  // submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLocation) {
      toast.error("Please select a location (search or click map).", {
        autoClose: 1200,
      });
      return;
    }

    const payload = {
      city,
      downtown,
      latitude: selectedLocation.lat,
      longitude: selectedLocation.lng,
      addressLine,
      customerId,
    };

    try {
      const res = await fetch(
        "https://goldenhides.in/api/user/add/delivery/address",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const body = await res.json();
      if (body.success) {
        toast.success(body.responseMessage || "Address added", {
          autoClose: 1200,
        });
        if (body.users && body.users[0])
          sessionStorage.setItem("active-user", JSON.stringify(body.users[0]));
        if (typeof onSubmit === "function") onSubmit(body);
        setTimeout(() => window.location.reload(), 900);
      } else {
        toast.error(body.responseMessage || "Failed to save address", {
          autoClose: 1400,
        });
      }
    } catch (err) {
      console.error("save address error", err);
      toast.error("Server error while saving address", { autoClose: 1400 });
    }
  };

  return (
    <Container
      fluid
      className="p-0"
      style={{ minHeight: "100vh", background: "#f7faf7" }}
    >
      {/* Page header */}
      <Row className="m-0">
        <Col className="p-3">
          <div className="d-flex align-items-center justify-content-between">
            <h3 className="mb-0 text-color" style={{ letterSpacing: 0.2 }}>
              Add Delivery Address
            </h3>
            <div>
              <small className="text-muted">
                Add a delivery address for faster checkout
              </small>
            </div>
          </div>
        </Col>
      </Row>

      {/* Map area */}
      <Row className="m-0">
        <Col xs={12} style={{ height: "62vh", padding: 0 }}>
          <div style={{ width: "100%", height: "100%" }}>
            <GoogleMap
              center={selectedLocation || initialCenter}
              zoom={selectedLocation ? 15 : 12}
              mapContainerStyle={{ width: "100%", height: "100%" }}
              onClick={onMapClick}
              onLoad={(map) => {
                mapRef.current = map;
              }}
            >
              {selectedLocation && <Marker position={selectedLocation} />}
            </GoogleMap>
          </div>
        </Col>
      </Row>

      {/* Floating form card (centered) */}
      <Row className="justify-content-center mt-4">
        <Col xs={12} md={9} lg={7}>
          <div
            className="card form-card rounded-card shadow-sm"
            style={{ padding: "1.25rem" }}
          >
            <Form onSubmit={handleSubmit}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0 text-color">Address Details</h5>
                  <div className="text-muted">
                    Search or click on map to set location
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setAddressLine("");
                      setCity("");
                      setDowntown("");
                      setSelectedLocation(null);
                      // clear place element input if available
                      try {
                        if (placeElementRef.current) {
                          const host = placeElementRef.current;
                          // attempt to clear the inner input if exposed
                          const innerInput =
                            host.querySelector && host.querySelector("input");
                          if (innerInput) innerInput.value = "";
                        }
                      } catch (e) {}
                    }}
                  >
                    Clear
                  </Button>

                  <Button
                    className="bg-color custom-bg-text"
                    onClick={useMyCurrentLocation}
                  >
                    Use my current location
                  </Button>
                </div>
              </div>

              <Form.Group className="mb-3" controlId="placeAutocomplete">
                {/* container for the PlaceAutocompleteElement */}
                <div
                  ref={autocompleteContainerRef}
                  className="mb-2"
                  style={{ width: "100%" }}
                />
                <Form.Text className="text-muted">
                  Pick from suggestions or refine address manually.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formAddressLine">
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Full address (house no., street, landmark)"
                  value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                  required
                  className="text-color"
                />
              </Form.Group>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                    className="text-color"
                  />
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Downtown / Locality"
                    value={downtown}
                    onChange={(e) => setDowntown(e.target.value)}
                    required
                    className="text-color"
                  />
                </Col>
              </Row>

              {selectedLocation && (
                <div className="mb-3 small text-muted">
                  Latitude: {selectedLocation.lat.toFixed(6)} &nbsp;•&nbsp;
                  Longitude: {selectedLocation.lng.toFixed(6)}
                </div>
              )}

              <div className="d-flex gap-3 justify-content-end">
                <Button
                  variant="secondary"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  className="bg-color custom-bg-text"
                  disabled={!selectedLocation}
                >
                  Add Location
                </Button>
              </div>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AddDeliveryAddress;
